import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Star, Shield, Clock, CheckCircle, Snowflake, WashingMachine, Utensils, Flame } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { BrandsSlider } from '../components/BrandsSlider';
import { Testimonials } from '../components/Testimonials';

// JSON-LD Schema for Vaughan Page
const vaughanSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Infinity Appliances Repair - Vaughan",
  "image": "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136",
  "telephone": "+1-647-919-6893",
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Vaughan",
    "addressRegion": "ON",
    "addressCountry": "CA"
  },
  "areaServed": [
    { "@type": "City", "name": "Vaughan" },
    { "@type": "City", "name": "Woodbridge" },
    { "@type": "City", "name": "Maple" },
    { "@type": "City", "name": "Kleinburg" }
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "150"
  }
};

const services = [
  { icon: Snowflake, title: 'Refrigerator Repair', description: 'Expert fridge diagnostics and repair' },
  { icon: WashingMachine, title: 'Washer & Dryer', description: 'Same-day laundry appliance service' },
  { icon: Utensils, title: 'Dishwasher Repair', description: 'Fix leaks and drainage issues' },
  { icon: Flame, title: 'Stove & Oven', description: 'Get cooking again fast' },
];

export const VaughanPage = () => {
  useEffect(() => {
    // Set SEO meta tags
    document.title = 'Appliance Repair Vaughan | Same-Day Service | Infinity';
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Expert appliance repair in Vaughan, Woodbridge & Maple. We fix Fridges, Washers, Dryers & Stoves today. Certified techs for Samsung, LG & Whirlpool. Call 647-919-6893.';

    // JSON-LD Schema
    let schemaScript = document.querySelector('#vaughan-schema');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'vaughan-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(vaughanSchema);

    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="vaughan-page">
      {/* Hero Section */}
      <section 
        className="hero-overlay relative min-h-[70vh] flex items-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1705578815676-11a1b22c00e3?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            {/* Breadcrumb */}
            <nav className="mb-6 text-white/70 text-sm">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Vaughan</span>
            </nav>

            {/* Trust Badge */}
            <div className="trust-badge mb-8">
              <MapPin size={18} className="text-[#FF6B00]" />
              <span className="text-white font-semibold">Serving Vaughan & Surrounding Areas</span>
            </div>

            {/* H1 */}
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Same-Day Appliance Repair in Vaughan
            </h1>
            <p className="text-2xl text-white/90 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Woodbridge & Maple
            </p>

            {/* Description */}
            <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl">
              Expert local service for homes near Vaughan Mills and the Kleinburg area. We offer 2-hour arrival windows for all Vaughan residents.
            </p>

            {/* CTA */}
            <a
              href="tel:647-919-6893"
              className="pulse-cta inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105 shadow-xl"
              style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
              data-testid="vaughan-hero-cta"
            >
              <Phone size={22} />
              <span>Call 647-919-6893</span>
            </a>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left Content */}
            <div>
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
              >
                Expert Appliance Repair in Vaughan
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-6">
                When your fridge stops cooling or your dryer won't heat in Vaughan, you need a local expert who knows the area. Infinity Appliances Repair provides rapid response times to homes near Vaughan Mills, Woodbridge, and Maple.
              </p>
              <p className="text-[#475569] leading-relaxed mb-8">
                We understand that a broken appliance disrupts your life, which is why we offer 2-hour arrival windows across the City of Vaughan. Our licensed technicians are specialized in Samsung, LG, and Bosch repairs—the brands most common in Vaughan homes.
              </p>

              {/* Features */}
              <div className="space-y-4 mb-8">
                {[
                  '2-Hour Arrival Windows',
                  'Licensed & Insured Technicians',
                  'Same-Day Repairs Available',
                  'All Major Brands Serviced',
                  'No Hidden Fees',
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-3">
                    <CheckCircle size={20} style={{ color: '#FF6B00' }} />
                    <span className="font-medium" style={{ color: '#1A2B4C' }}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="tel:647-919-6893"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full font-bold text-white transition-transform hover:scale-105"
                style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
                data-testid="vaughan-content-cta"
              >
                <Phone size={18} />
                <span>Call for Vaughan Same-Day Service</span>
              </a>
            </div>

            {/* Right - Map */}
            <div>
              <div className="map-container rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d92152.35721892929!2d-79.5076!3d43.8563!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b2581a9b50e31%3A0x1e8c3899c3b63fb6!2sVaughan%20Mills!5e0!3m2!1sen!2sca!4v1710000000001"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Vaughan Service Area Map"
                  data-testid="vaughan-map"
                ></iframe>
              </div>
              <p className="text-center text-[#475569] mt-4 text-sm">
                Serving all Vaughan neighborhoods including Woodbridge, Maple, Kleinburg, and Concord
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-[#F8F9FC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 
            className="text-2xl font-bold text-center mb-10"
            style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
          >
            Our Vaughan Repair Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={service.title}
                  className="glass-card border-0 shadow-lg hover:shadow-xl transition-shadow"
                  data-testid={`vaughan-service-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <CardContent className="p-6 text-center">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                      style={{ background: 'rgba(255, 107, 0, 0.1)' }}
                    >
                      <IconComponent size={28} style={{ color: '#FF6B00' }} />
                    </div>
                    <h3 
                      className="font-bold mb-2"
                      style={{ fontFamily: 'Montserrat, sans-serif', color: '#1A2B4C' }}
                    >
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#475569]">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <BrandsSlider />
      <Testimonials />

      {/* Final CTA */}
      <section className="py-16" style={{ background: '#1A2B4C' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl font-bold text-white mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Need Appliance Repair in Vaughan?
          </h2>
          <p className="text-white/70 mb-8 text-lg">
            Our Vaughan technicians are ready to help. Same-day service available.
          </p>
          <a
            href="tel:647-919-6893"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-lg text-white transition-transform hover:scale-105"
            style={{ background: '#FF6B00', fontFamily: 'Montserrat, sans-serif' }}
            data-testid="vaughan-final-cta"
          >
            <Phone size={22} />
            <span>Call 647-919-6893 Now</span>
          </a>
        </div>
      </section>
    </main>
  );
};

export default VaughanPage;
